import { COLORS } from '@/styles/color';
import { inputBaseStyle, inputErrorStyle } from '@/styles/components/shared-input-styles';
import { FONT_WEIGHTS, TEXT_SIZES } from '@/styles/typography';
import { remCalc } from '@/styles/typography/utils';
import { ChangeEvent, FocusEvent, forwardRef, KeyboardEvent, useId } from 'react';
import styled, { css } from 'styled-components';

export const InputFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const InputFieldLabel = styled.label`
  ${TEXT_SIZES[16]};

  display: inline-block;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  padding-bottom: ${remCalc(8)};
  align-self: flex-start;
`;

export const InputFieldMessage = styled.span`
  ${TEXT_SIZES[13]};
  margin-top: ${remCalc(6)};
  color: ${COLORS.GRAY.BASE};
`;

export interface InputFieldProps extends PropsWithClassName {
  name: string;
  label?: string;
  placeholder?: string;
  invalid?: boolean;
  errorMessage?: string;
  warningMessage?: string;
  helpMessage?: string;
  value?: string;
  translucent?: boolean;
  disabled?: boolean;
  loading?: boolean;
  rows?: number;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement>) => void;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onValueChange?: (value: string) => void;
  onEnterKey?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
}

const Textarea = forwardRef<HTMLTextAreaElement, InputFieldProps>((props, ref) => {
  const id = useId();

  const isInvalid = props.invalid && !!props.errorMessage;
  const hasWarning = !isInvalid && !!props.warningMessage;

  return (
    <InputFieldWrapper className={props.className}>
      {props.label && <InputFieldLabel htmlFor={id}>{props.label}</InputFieldLabel>}

      <TextareaElement
        ref={ref}
        id={id}
        name={props.name}
        placeholder={props.placeholder}
        value={props.value}
        onChange={(e) => {
          if (props.onChange) props.onChange(e);
          if (props.onValueChange) props.onValueChange(e.target.value);
        }}
        onBlur={props.onBlur}
        rows={props.rows}
        invalid={props.invalid}
        translucent={props.translucent}
        disabled={props.disabled}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && props.onEnterKey) {
            props.onEnterKey(e);
          }
        }}
      />

      {isInvalid ? (
        <InputFieldMessage style={{ color: COLORS.RED.LOWLIGHT }}>{props.errorMessage}</InputFieldMessage>
      ) : hasWarning ? (
        <InputFieldMessage style={{ color: COLORS.ORANGE.LOWLIGHT }}>{props.warningMessage}</InputFieldMessage>
      ) : !!props.helpMessage ? (
        <InputFieldMessage>{props.helpMessage}</InputFieldMessage>
      ) : null}
    </InputFieldWrapper>
  );
});

Textarea.displayName = 'Textarea';

export default Textarea;

const TextareaElement = styled.textarea<InputFieldProps>`
  ${inputBaseStyle};
  height: auto;
  resize: none;

  ${(props) => props.invalid && inputErrorStyle};

  ${(props) =>
    props.translucent &&
    css`
      mix-blend-mode: multiply;
    `};
`;

TextareaElement.defaultProps = {
  invalid: false,
};
